// Renders a chart spec emitted by the rhinoanalyst service's render_chart tool.
// Uses Recharts with the same palette as the VoIP Reports page so charts in the
// drawer match the report beside them.

import React from 'react';
import PropTypes from 'prop-types';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

// Named series get their report colour; everything else cycles the fallbacks.
const NAMED_COLORS = {
  inbound: '#3D9BC9',
  outbound: '#F4C430',
  answered: '#5DBE85',
  voicemail: '#F37846',
  missed: '#A60B4F',
  'no answer': '#FFC274',
  busy: '#4FBFA8',
  failed: '#E94B4F',
};
const FALLBACK_COLORS = ['#3D9BC9', '#F4C430', '#5DBE85', '#F37846', '#A60B4F', '#FB9C57', '#B0E78C', '#FFD78F'];

const colorFor = (name, idx) => {
  const key = (name || '').toLowerCase();
  const hit = Object.keys(NAMED_COLORS).find((k) => key.includes(k));
  return hit ? NAMED_COLORS[hit] : FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
};

const formatValue = (value, format) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '';
  const n = Number(value);
  if (format === 'percent') return `${(n <= 1 ? n * 100 : n).toFixed(n <= 1 ? 1 : 0)}%`;
  if (format === 'seconds') return n >= 90 ? `${(n / 60).toFixed(1)} min` : `${Math.round(n)} s`;
  return Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const ReggieChart = ({ spec, width, height }) => {
  const {
    title, chart_type: chartType, x_key: xKey, series, rows, y_label: yLabel, value_format: valueFormat,
  } = spec;
  if (!Array.isArray(rows) || !rows.length || !Array.isArray(series) || !series.length) return null;

  const fmt = (v) => formatValue(v, valueFormat);
  const tooltipFormatter = (value, name) => [fmt(value), name];

  let body;
  if (chartType === 'pie') {
    const s = series[0];
    const data = rows.map((r) => ({ name: String(r[xKey]), value: Number(r[s.key]) || 0 }));
    body = (
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius="45%" outerRadius="80%" paddingAngle={2}>
          {data.map((d, i) => <Cell key={d.name} fill={colorFor(d.name, i)} />)}
        </Pie>
        <Tooltip formatter={tooltipFormatter} />
        <Legend />
      </PieChart>
    );
  } else if (chartType === 'line') {
    body = (
      <LineChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={fmt} label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', fontSize: 11 } : undefined} />
        <Tooltip formatter={tooltipFormatter} />
        {series.length > 1 && <Legend />}
        {series.map((s, i) => (
          <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={colorFor(s.name, i)} strokeWidth={2} dot={rows.length <= 31} />
        ))}
      </LineChart>
    );
  } else {
    body = (
      <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} interval={rows.length > 12 ? 'preserveStartEnd' : 0} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={fmt} label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', fontSize: 11 } : undefined} />
        <Tooltip formatter={tooltipFormatter} />
        {series.length > 1 && <Legend />}
        {series.map((s, i) => (
          <Bar key={s.key} dataKey={s.key} name={s.name} fill={colorFor(s.name, i)} radius={[4, 4, 0, 0]} />
        ))}
      </BarChart>
    );
  }

  return (
    <div className="rhinoanalyst__chart" data-cypress="reggieChart">
      <div className="rhinoanalyst__chart__title">{title}</div>
      <div className="rhinoanalyst__chart__body">
        {width ? (
          // Fixed size for offscreen rendering (PDF); ResponsiveContainer needs a laid-out parent.
          React.cloneElement(body, { width, height: height || 240 })
        ) : (
          <ResponsiveContainer width="100%" height={height || 220}>
            {body}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

ReggieChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  spec: PropTypes.shape({
    title: PropTypes.string,
    chart_type: PropTypes.oneOf(['bar', 'line', 'pie']),
    x_key: PropTypes.string,
    series: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, name: PropTypes.string })),
    rows: PropTypes.arrayOf(PropTypes.object),
    y_label: PropTypes.string,
    value_format: PropTypes.oneOf(['number', 'percent', 'seconds']),
  }).isRequired,
};

ReggieChart.defaultProps = {
  width: null,
  height: null,
};

export default ReggieChart;
